import React, { useCallback, useEffect, useMemo, useState, useRef, createContext, useContext } from 'react';
import { useAuth } from './AuthContext';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderRole: 'admin' | 'employee';
  senderCompany?: string;
  text: string;
  timestamp: string;
  read: boolean;
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
}
export interface Conversation {
  id: string;
  participantName: string;
  participantCompany: string;
  lastMessage: string;
  lastTimestamp: string;
  unreadCount: number;
  status: 'open' | 'closed';
  closedAt?: string;
  closedBy?: string;
}
export interface EmployeeInfo {
  id: string;
  name: string;
  company: string;
  branch: string;
  companyId?: number;
  branchId?: string;
}
export const QUICK_REPLIES = [{
  label: '👋 Saludo',
  text: '¡Hola! ¿En qué puedo ayudarte hoy?'
}, {
  label: '📦 Estado pedido',
  text: 'Tu pedido está siendo preparado. Te avisaremos cuando esté listo.'
}, {
  label: '✅ Pedido listo',
  text: '¡Tu pedido está listo para entrega! 🎉'
}, {
  label: '🕐 Espera',
  text: 'Estamos revisando tu caso. Te responderemos en breve.'
}, {
  label: '🍽️ Menú',
  text: 'El menú de hoy ya está disponible. Puedes verlo en la sección "Hacer Pedido".'
}, {
  label: '🔧 Problema resuelto',
  text: 'El problema ha sido resuelto. ¿Hay algo más en lo que pueda ayudarte?'
}];
interface ChatContextType {
  messages: Message[];
  conversations: Conversation[];
  sendMessage: (text: string, conversationId?: string, file?: File) => void;
  markAsRead: (conversationId: string) => void;
  deleteConversation: (conversationId: string) => void;
  closeConversation: (conversationId: string) => void;
  reopenConversation: (conversationId: string) => void;
  startConversation: (employee: EmployeeInfo, initialMessage: string) => void;
  getEmployeeList: () => Promise<EmployeeInfo[]>;
  setTyping: (conversationId: string) => void;
  typingIn: Set<string>;
  unreadTotal: number;
  loading: boolean;
  uploading: boolean;
}
const ChatContext = createContext<ChatContextType | undefined>(undefined);
// ── Sound helper ──
let notifAudioCtx: AudioContext | null = null;
const ensureNotifAudioCtx = () => {
  if (!notifAudioCtx || notifAudioCtx.state === 'closed') {
    notifAudioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (notifAudioCtx.state === 'suspended') {
    notifAudioCtx.resume();
  }
  return notifAudioCtx;
};
const playNotificationSound = () => {
  try {
    const ctx = ensureNotifAudioCtx();
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(1047, ctx.currentTime);
    gain1.gain.setValueAtTime(0.15, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 0.2);
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1319, ctx.currentTime + 0.12);
    gain2.gain.setValueAtTime(0.18, ctx.currentTime + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(ctx.currentTime + 0.12);
    osc2.stop(ctx.currentTime + 0.4);
  } catch (e) {}
};
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
export function ChatProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user
  } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [useSupabase, setUseSupabase] = useState(false);
  const [typingIn, setTypingIn] = useState<Set<string>>(new Set());
  // Cache de nombres reales de empleados (id → {name, company})
  const [employeeNamesCache, setEmployeeNamesCache] = useState<Map<string, {
    name: string;
    company: string;
  }>>(new Map());
  const [closedConversations, setClosedConversations] = useState<Map<string, {
    closedAt: string;
    closedBy: string;
  }>>(() => {
    try {
      const saved = localStorage.getItem('criket_closed_conversations');
      return saved ? new Map(JSON.parse(saved)) : new Map();
    } catch {
      return new Map();
    }
  });
  const prevMessageCountRef = useRef(0);
  const initialLoadDoneRef = useRef(false);
  const markAsReadDebounce = useRef<Record<string, NodeJS.Timeout>>({});
  const closedSyncRef = useRef(false);
  // Persist closed conversations to localStorage + Supabase
  useEffect(() => {
    localStorage.setItem('criket_closed_conversations', JSON.stringify(Array.from(closedConversations.entries())));
    // Sync to Supabase (skip initial load sync)
    if (closedSyncRef.current && useSupabase) {
      const obj = Object.fromEntries(closedConversations);
      supabaseClient.saveChatClosedConversations(obj);
    }
  }, [closedConversations, useSupabase]);
  // Warm up audio
  useEffect(() => {
    const warmUp = () => {
      ensureNotifAudioCtx();
      document.removeEventListener('touchstart', warmUp);
      document.removeEventListener('click', warmUp);
    };
    document.addEventListener('touchstart', warmUp, {
      once: true
    });
    document.addEventListener('click', warmUp, {
      once: true
    });
    return () => {
      document.removeEventListener('touchstart', warmUp);
      document.removeEventListener('click', warmUp);
    };
  }, []);
  // ── Load messages ──
  useEffect(() => {
    const loadMessages = async () => {
      setLoading(true);
      if (isSupabaseConfigured()) {
        try {
          // Cargar nombres reales de empleados desde Supabase
          try {
            const employees = await supabaseClient.getEmployees();
            const companies = await supabaseClient.getCompanies();
            const namesMap = new Map<string, {
              name: string;
              company: string;
            }>();
            employees.forEach((emp: any) => {
              const company = companies.find((c: any) => c.id === emp.company_id);
              namesMap.set(emp.id, {
                name: emp.name,
                company: company?.name || ''
              });
            });
            setEmployeeNamesCache(namesMap);
          } catch (e) {
            console.error('[Chat] Error cargando nombres de empleados:', e);
          }
          // Load closed conversations from Supabase
          try {
            const closedData = await supabaseClient.getChatClosedConversations();
            if (closedData && Object.keys(closedData).length > 0) {
              setClosedConversations(new Map(Object.entries(closedData)));
            }
          } catch (e) {
            console.error('[Chat] Error loading closed conversations:', e);
          }
          const data = await supabaseClient.getChatMessages();
          if (data !== null) {
            console.log(`[Chat] ✅ Supabase conectado. ${data.length} mensajes.`);
            setMessages(data);
            setUseSupabase(true);
            prevMessageCountRef.current = data.length;
            initialLoadDoneRef.current = true;
            closedSyncRef.current = true;
            setLoading(false);
            return;
          }
        } catch (e: any) {
          console.error('[Chat] ❌ Error Supabase:', e?.message || e);
        }
      }
      const saved = localStorage.getItem('criket_chat_messages');
      const parsed = saved ? JSON.parse(saved) : [];
      setMessages(parsed);
      prevMessageCountRef.current = parsed.length;
      initialLoadDoneRef.current = true;
      closedSyncRef.current = true;
      setLoading(false);
    };
    loadMessages();
  }, [user?.id]);
  // ── Real-time subscription ──
  useEffect(() => {
    if (!useSupabase || !isSupabaseConfigured()) return;
    let subscription: any = null;
    let pollInterval: NodeJS.Timeout | null = null;
    try {
      subscription = supabaseClient.subscribeToChatMessages((payload: any) => {
        if (payload.eventType === 'INSERT') {
          const msg = payload.new;
          const newMessage: Message = {
            id: msg.id,
            conversationId: msg.conversation_id,
            senderId: msg.sender_id,
            senderName: msg.sender_name,
            senderRole: msg.sender_role,
            senderCompany: msg.sender_company || '',
            text: msg.text,
            timestamp: msg.created_at,
            read: msg.read || false,
            fileUrl: msg.file_url || undefined,
            fileName: msg.file_name || undefined,
            fileType: msg.file_type || undefined,
            fileSize: msg.file_size || undefined
          };
          setMessages(prev => {
            if (prev.some(m => m.id === newMessage.id)) return prev;
            return [...prev, newMessage];
          });
        } else if (payload.eventType === 'UPDATE') {
          const msg = payload.new;
          setMessages(prev => {
            let changed = false;
            const updated = prev.map(m => {
              if (m.id === msg.id && m.read !== msg.read) {
                changed = true;
                return {
                  ...m,
                  read: msg.read
                };
              }
              return m;
            });
            return changed ? updated : prev;
          });
        } else if (payload.eventType === 'DELETE') {
          const msg = payload.old;
          setMessages(prev => prev.filter(m => m.id !== msg.id));
        }
      });
    } catch (e: any) {
      console.error('[Chat] ❌ Real-time error:', e?.message || e);
    }
    // Polling fallback: refresh messages + closed status every 8 seconds
    pollInterval = setInterval(async () => {
      try {
        const data = await supabaseClient.getChatMessages();
        if (data !== null) {
          setMessages(prev => {
            if (data.length !== prev.length || data.length > 0 && prev.length > 0 && data[data.length - 1]?.id !== prev[prev.length - 1]?.id) {
              return data;
            }
            return prev;
          });
        }
        // Also poll closed conversation status
        const closedData = await supabaseClient.getChatClosedConversations();
        if (closedData) {
          setClosedConversations(prev => {
            const newMap = new Map(Object.entries(closedData));
            // Only update if different
            if (prev.size !== newMap.size || [...newMap].some(([k, v]) => {
              const existing = prev.get(k);
              return !existing || existing.closedAt !== v.closedAt;
            })) {
              return newMap;
            }
            return prev;
          });
        }
      } catch (e) {
        // Silent fail for polling
      }
    }, 8000);
    return () => {
      if (subscription) {
        try {
          subscription.unsubscribe();
        } catch (e) {}
      }
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [useSupabase]);
  // ── Play sound on new incoming messages ──
  useEffect(() => {
    if (!initialLoadDoneRef.current || !user) return;
    if (messages.length > prevMessageCountRef.current) {
      const newest = messages[messages.length - 1];
      if (newest && newest.senderId !== user.id) {
        const isRelevant = user.role === 'admin' ? newest.senderRole === 'employee' : newest.conversationId === user.id && newest.senderRole === 'admin';
        if (isRelevant) playNotificationSound();
      }
    }
    prevMessageCountRef.current = messages.length;
  }, [messages, user]);
  // ── Save to localStorage as fallback ──
  useEffect(() => {
    if (!useSupabase && messages.length > 0) {
      localStorage.setItem('criket_chat_messages', JSON.stringify(messages));
    }
  }, [messages, useSupabase]);
  // ── Derived conversations ──
  const conversations = useMemo(() => {
    const convMap = new Map<string, Conversation>();
    messages.forEach(msg => {
      const convId = msg.conversationId;
      if (!convMap.has(convId)) {
        const closedInfo = closedConversations.get(convId);
        // Intentar resolver nombre real del empleado desde el cache
        const cachedEmployee = employeeNamesCache.get(convId);
        convMap.set(convId, {
          id: convId,
          participantName: cachedEmployee?.name || 'Usuario',
          participantCompany: cachedEmployee?.company || '',
          lastMessage: '',
          lastTimestamp: '',
          unreadCount: 0,
          status: closedInfo ? 'closed' : 'open',
          closedAt: closedInfo?.closedAt,
          closedBy: closedInfo?.closedBy
        });
      }
      const conv = convMap.get(convId)!;
      // Solo usar senderName del mensaje si NO tenemos el nombre real en cache
      if (msg.senderRole === 'employee') {
        if (!employeeNamesCache.has(convId)) {
          conv.participantName = msg.senderName;
        }
        if (msg.senderCompany && !employeeNamesCache.has(convId)) {
          conv.participantCompany = msg.senderCompany;
        }
      }
      if (!conv.lastTimestamp || new Date(msg.timestamp) > new Date(conv.lastTimestamp)) {
        conv.lastMessage = msg.fileUrl ? msg.fileName ? `📎 ${msg.fileName}` : '📎 Archivo' : msg.text;
        conv.lastTimestamp = msg.timestamp;
      }
      if (msg.senderRole === 'employee' && !msg.read) {
        conv.unreadCount += 1;
      }
    });
    return Array.from(convMap.values()).sort((a, b) => new Date(b.lastTimestamp).getTime() - new Date(a.lastTimestamp).getTime());
  }, [messages, closedConversations, employeeNamesCache]);
  // ── Send message ──
  const sendMessage = useCallback(async (text: string, targetConversationId?: string, file?: File) => {
    if (!user) return;
    const conversationId = targetConversationId || (user.role === 'employee' ? user.id : null);
    if (!conversationId) return;
    // Block employee from sending to closed conversations
    if (user.role === 'employee' && closedConversations.has(conversationId)) {
      return;
    }
    if (file && file.size > MAX_FILE_SIZE) {
      alert('El archivo es muy grande. El límite es 5MB.');
      return;
    }
    let fileUrl: string | undefined;
    let fileName: string | undefined;
    let fileType: string | undefined;
    let fileSize: number | undefined;
    if (file) {
      setUploading(true);
      try {
        if (useSupabase) {
          const url = await supabaseClient.uploadChatFile(file, conversationId);
          if (url) {
            fileUrl = url;
            fileName = file.name;
            fileType = file.type;
            fileSize = file.size;
          }
        } else {
          if (file.size <= 1024 * 1024) {
            const dataUrl = await new Promise<string>(resolve => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(file);
            });
            fileUrl = dataUrl;
            fileName = file.name;
            fileType = file.type;
            fileSize = file.size;
          } else {
            alert('Para archivos mayores a 1MB necesitas Supabase configurado.');
            setUploading(false);
            return;
          }
        }
      } catch (e: any) {
        console.error('[Chat] Error uploading file:', e);
        alert('Error al subir el archivo. Intenta de nuevo.');
        setUploading(false);
        return;
      }
      setUploading(false);
    }
    const msgText = text || (fileName ? '' : '');
    const newMessage: Message = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      conversationId,
      senderId: user.id,
      senderName: user.name,
      senderRole: user.role,
      senderCompany: user.company || '',
      text: msgText,
      timestamp: new Date().toISOString(),
      read: false,
      fileUrl,
      fileName,
      fileType,
      fileSize
    };
    if (useSupabase) {
      try {
        await supabaseClient.createChatMessage(newMessage);
        setMessages(prev => {
          if (prev.some(m => m.id === newMessage.id)) return prev;
          return [...prev, newMessage];
        });
        return;
      } catch (e: any) {
        console.error('[Chat] ❌ Error sending:', e?.message || e);
      }
    }
    setMessages(prev => [...prev, newMessage]);
  }, [user, useSupabase, closedConversations]);
  // ── Mark as read (debounced) ──
  const markAsRead = useCallback((conversationId: string) => {
    if (!user) return;
    if (markAsReadDebounce.current[conversationId]) {
      clearTimeout(markAsReadDebounce.current[conversationId]);
    }
    setMessages(prev => {
      let changed = false;
      const updated = prev.map(msg => {
        if (user.role === 'admin' && msg.conversationId === conversationId && msg.senderRole === 'employee' && !msg.read) {
          changed = true;
          return {
            ...msg,
            read: true
          };
        }
        if (user.role === 'employee' && msg.conversationId === user.id && msg.senderRole === 'admin' && !msg.read) {
          changed = true;
          return {
            ...msg,
            read: true
          };
        }
        return msg;
      });
      return changed ? updated : prev;
    });
    if (useSupabase) {
      markAsReadDebounce.current[conversationId] = setTimeout(async () => {
        try {
          await supabaseClient.markChatMessagesAsRead(conversationId, user.role);
        } catch (e) {
          console.error('Failed to mark as read:', e);
        }
      }, 500);
    }
  }, [user, useSupabase]);
  // ── Close conversation ──
  const closeConversation = useCallback((conversationId: string) => {
    if (!user) return;
    setClosedConversations(prev => {
      const next = new Map(prev);
      next.set(conversationId, {
        closedAt: new Date().toISOString(),
        closedBy: user.name
      });
      return next;
    });
  }, [user]);
  // ── Reopen conversation ──
  const reopenConversation = useCallback((conversationId: string) => {
    setClosedConversations(prev => {
      const next = new Map(prev);
      next.delete(conversationId);
      return next;
    });
  }, []);
  // ── Delete conversation ──
  const deleteConversation = useCallback(async (conversationId: string) => {
    if (useSupabase) {
      try {
        await supabaseClient.deleteChatConversation(conversationId);
      } catch (e) {
        console.error('Failed to delete conversation:', e);
      }
    }
    setMessages(prev => prev.filter(m => m.conversationId !== conversationId));
    // Also remove from closed
    setClosedConversations(prev => {
      if (prev.has(conversationId)) {
        const next = new Map(prev);
        next.delete(conversationId);
        return next;
      }
      return prev;
    });
  }, [useSupabase]);
  // ── Start new conversation (admin → employee) ──
  const startConversation = useCallback((employee: EmployeeInfo, initialMessage: string) => {
    if (!user || user.role !== 'admin') return;
    // Use employee ID as conversation ID (same as when employee starts)
    const conversationId = employee.id;
    // Check if conversation already exists
    const exists = messages.some(m => m.conversationId === conversationId);
    const newMessage: Message = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      conversationId,
      senderId: user.id,
      senderName: user.name,
      senderRole: 'admin',
      senderCompany: '',
      text: initialMessage,
      timestamp: new Date().toISOString(),
      read: false
    };
    if (useSupabase) {
      supabaseClient.createChatMessage(newMessage).then(() => {
        setMessages(prev => {
          if (prev.some(m => m.id === newMessage.id)) return prev;
          return [...prev, newMessage];
        });
      }).catch(e => {
        console.error('[Chat] Error starting conversation:', e);
        // Fallback to local
        setMessages(prev => [...prev, newMessage]);
      });
    } else {
      setMessages(prev => [...prev, newMessage]);
    }
  }, [user, useSupabase, messages]);
  // ── Get employee list for new conversation ──
  const getEmployeeList = useCallback(async (): Promise<EmployeeInfo[]> => {
    if (isSupabaseConfigured()) {
      try {
        const employees = await supabaseClient.getEmployees();
        const companies = await supabaseClient.getCompanies();
        return employees.filter((emp: any) => emp.status !== 'inactive' && emp.status !== 'disabled').map((emp: any) => {
          const company = companies.find((c: any) => c.id === emp.company_id);
          const branch = company?.branches?.find((b: any) => b.id === emp.branch_id);
          return {
            id: emp.id,
            name: emp.name,
            company: company?.name || 'Sin empresa',
            branch: branch?.name || 'Principal',
            companyId: emp.company_id,
            branchId: emp.branch_id
          };
        });
      } catch (e) {
        console.error('[Chat] Error fetching employees:', e);
      }
    }
    return [];
  }, []);
  // ── Unread total ──
  const unreadTotal = useMemo(() => {
    if (!user) return 0;
    if (user.role === 'admin') {
      return messages.filter(m => m.senderRole === 'employee' && !m.read).length;
    }
    return messages.filter(m => m.conversationId === user.id && m.senderRole === 'admin' && !m.read).length;
  }, [user, messages]);
  // ── Typing indicator via localStorage ──
  const setTyping = useCallback((conversationId: string) => {
    if (!user) return;
    const key = `criket_typing_${conversationId}_${user.role}`;
    localStorage.setItem(key, Date.now().toString());
  }, [user]);
  // Listen for typing events from the other party
  useEffect(() => {
    if (!user) return;
    const otherRole = user.role === 'admin' ? 'employee' : 'admin';
    const checkTyping = () => {
      const now = Date.now();
      const newTyping = new Set<string>();
      if (user.role === 'admin') {
        // Admin checks all conversations for employee typing
        conversations.forEach(conv => {
          const key = `criket_typing_${conv.id}_employee`;
          const ts = localStorage.getItem(key);
          if (ts && now - parseInt(ts) < 3000) {
            newTyping.add(conv.id);
          }
        });
      } else {
        // Employee checks their own conversation for admin typing
        const key = `criket_typing_${user.id}_admin`;
        const ts = localStorage.getItem(key);
        if (ts && now - parseInt(ts) < 3000) {
          newTyping.add(user.id);
        }
      }
      setTypingIn(prev => {
        if (prev.size !== newTyping.size || [...newTyping].some(id => !prev.has(id))) {
          return newTyping;
        }
        return prev;
      });
    };
    // Check on storage events (cross-tab)
    const handleStorage = (e: StorageEvent) => {
      if (e.key?.startsWith('criket_typing_')) {
        checkTyping();
      }
    };
    window.addEventListener('storage', handleStorage);
    // Poll every 1s for same-tab updates
    const interval = setInterval(checkTyping, 1000);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, [user, conversations]);
  return <ChatContext.Provider value={{
    messages,
    conversations,
    sendMessage,
    markAsRead,
    deleteConversation,
    closeConversation,
    reopenConversation,
    startConversation,
    getEmployeeList,
    setTyping,
    typingIn,
    unreadTotal,
    loading,
    uploading
  }} data-id="element-1030">
      {children}
    </ChatContext.Provider>;
}
export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}